import { ComponentProps, FunctionComponent } from 'react';
import { connect, useSelector, type ConnectedProps } from 'react-redux';
import { GlobalBar } from '@yandex-360/global-bar';

import type { ReduxState } from '../../store';
import styles from './GlobalBar.module.css';
import {
    selectApplicationByKey,
    selectAuthEnabled,
    selectGlobalBarInitData,
    selectIsB2bAdmin,
    selectOrganizationLogoUrl,
    selectServices,
    selectUserIdHost,
} from '@/store/selectors';

// import ya360DiskSolidMd from '@yandex-360/iconostasis/icons/v2/ya-360-disk-solid-md.svg';
// import ya360MailSolidMd from '@yandex-360/iconostasis/icons/v2/ya-360-mail-solid-md.svg';

const mapStateToProps = (state: ReduxState) => {
    const {
        config: {
            domain,
            urlsBase,
            environment,
            language,
            links: {
                passport: { auth: authLink },
            },
        },
        user: { user },
    } = state;

    const themeName = 'light';

    return {
        urlsBase,
        themeName,
        authLink,
        domain,
        environment,
        language,
        user,
    };
};

const connector = connect(mapStateToProps);

type OnPremGlobalBarProps = ConnectedProps<typeof connector>;

const getUserEnv = (environment: string): 'prod' | 'test' => {
    return environment === 'production' ? 'prod' : 'test';
};

const OnPremGlobalBar: FunctionComponent<OnPremGlobalBarProps> = ({
    authLink,
    environment,
    language,
    user,
}) => {
    const userIdHost = useSelector(selectUserIdHost);
    const authEnabled = useSelector(selectAuthEnabled);
    const initData = useSelector(selectGlobalBarInitData);
    const organizationLogoUrl = useSelector(selectOrganizationLogoUrl);
    const isB2bAdmin = useSelector(selectIsB2bAdmin);
    const services = useSelector(selectServices)?.filter(service => service !== 'Documents');
    const adminUrl = useSelector(selectApplicationByKey('admin'))?.entrypoints?.web || '/admin';
    const diskUrl = useSelector(selectApplicationByKey('disk'))?.entrypoints?.web || '';
    const docsUrl = useSelector(selectApplicationByKey('docs'))?.entrypoints?.web || '';
    const boardsUrl = useSelector(selectApplicationByKey('boards'))?.entrypoints?.web || '';
    const wikiUrl = useSelector(selectApplicationByKey('wiki'))?.entrypoints?.web || '';
    const trackerUrl = useSelector(selectApplicationByKey('tracker'))?.entrypoints?.web || '';
    const formsUrl = useSelector(selectApplicationByKey('forms'))?.entrypoints?.web || '';

    const userEnv = getUserEnv(environment);

    const userIdProps = authEnabled && user
        ? {
            uid: user.uid,
            userName: user.displayName || user.login,
            userProps: {
                env: userEnv,
                host: userIdHost,
                queryParams: {
                    // Скрыть в попапе пункт «О программе»: он ведёт на лендинг,
                    // то есть на текущий же сайт. Параметр поддержан виджетом
                    // user-id-on-premise: https://a.yandex-team.ru/review/15403346
                    hideAbout: '1',
                },
                userData: {
                    avatarId: user.avatarId,
                    plus: user.hasProSubscription,
                },
            },
        }
        : {};

    const handleLoginClick = () => {
        if (typeof window !== 'undefined' && authLink) {
            window.location.assign(authLink);
        }
    };

    const props: ComponentProps<typeof GlobalBar> = {
        countMetrikaFn: () => {},
        currentServiceLink: '/',
        timeZone: '',
        lang: language,
        tld: 'ru',
        isB2bAdmin,
        hideTuning: true,
        allowedServices: isB2bAdmin && !services.includes('Admin')
            ? [...services, 'Admin']
            : services,
        isB2bUser: true,
        isOnPrem: true,
        initData: initData
            ? { ...initData, show_monetization: false, animate_monetization: false }
            : initData,
        ...(organizationLogoUrl ? { logoProps: { imageUrl: organizationLogoUrl } } : {}),
        logEnv: getUserEnv(environment) === 'prod' ? 'production' : 'development',
        ...(authEnabled ? { onLoginClick: handleLoginClick } : {}),
        linksOverride: {
            Disk: diskUrl,
            Mail: '/mail',
            Telemost: '/telemost',
            Calendar: '/calendar',
            Notes: '/notes',
            Concept: boardsUrl,
            Contact: '/contact',
            Messenger: '/messenger',
            Admin: adminUrl,
            Documents: docsUrl,
            Fan: '/fan',
            Tracker: trackerUrl,
            Wiki: wikiUrl,
            Forms: formsUrl,
            AlicePro: '/alicepro',
        },
        ...userIdProps,
    };

    return (
        <GlobalBar {...props} className={styles.globalBar}/>
    );
};

export default connector(OnPremGlobalBar);
